import React from "react";
import l from "components/util/l";
import WSManager from "components/util/ws";
import IsometricImage from "components/util/helpers/icons/IsometricImage";
import { ScreenshotHelperOverlay } from "components/util/helpers/screenshot_helper/ScreenshotHelper";
import ConversationCamera from "./Conversation.sub.camera.view";
import ConversationResult from "./Conversation.sub.result.view";
import ConversationExecution from "./Conversation.sub.execution.view";
import ConversationInput from "./Conversation.sub.input.view";
import ConversationMessage from "./Conversation.sub.message.view";
import ConversationStreamingMessage from "./Conversation.sub.streaming.view";
import "styles/css/agent/Chat.css";

const DEFAULT_AGENT_NAME = "Workix";

const ConversationView = ({
  activityText,
  attachmentOptions = [],
  cameraRequest,
  chatRoomName,
  chatSettings,
  executionRoomName,
  flowId,
  formRef,
  historyComponent,
  lastBotMessage,
  lastMessage,
  listRef,
  messages,
  models = [],
  rulesComponent,
  screenshotRequest,
  scrollDirection,
  streamingText,
  thinkingText,
  isHighlightEnabled = false,
  isStopShown,
  isDeveloper,
  isDraggingFiles = false,
  isHistoryEnabled = false,
  isLoading,
  isNewChatShown = false,
  isPreview,
  isReadOnly = false,
  isStreaming,
  isThinkingShown,
  takePhoto = () => { },
  takeScreenshot = () => { },
  onChatMessageReceived = () => { },
  onChatScroll = () => { },
  onClickCancelChatAction = () => { },
  onClickNewChat = () => { },
  onClickRegenerateMessage = () => { },
  onClickRepeatExecution = () => { },
  onClickRollbackExecution = () => { },
  onClickScrollBtn = () => { },
  onClickToggleHistory = () => { },
  onClickToggleRules = () => { },
  onDragEnterChat = () => { },
  onDragLeaveFiles = () => { },
  onDropFiles = () => { },
  onExecutionMessageReceived = () => { },
  onFinishPhoto = () => { },
  onFinishScreenshot = () => { },
  onSendMessage = () => { },
}) => (
  <div id="chat" className="d-flex justify-content-center w-100 h-100" onDragEnter={onDragEnterChat}>

    <div className="chat-squares d-flex align-items-end align-items-lg-start">
      <IsometricImage />
    </div>

    <div id="chat-content">

      {/* What the conversation can do to itself: start over, or go back to an earlier one */}
      {(isNewChatShown || isHistoryEnabled) &&
        <div className="chat-header">

          {isNewChatShown &&
            <button
              type="button"
              className="chat-header-btn"
              onClick={onClickNewChat}>
              <i className="fas fa-message mr-2" />
              {l("Nuevo chat%PA047")}
            </button>
          }

          {isHistoryEnabled &&
            <div className="hs-unfold">
              <button
                type="button"
                id="chat-header-dropdown-button"
                className={"chat-header-btn" + ((historyComponent || rulesComponent) ? " active" : "")}
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false">
                <i className="fas fa-ellipsis-v" />
              </button>
              <div className="dropdown-menu dropdown-menu-right" aria-labelledby="chat-header-dropdown-button">
                <a className="dropdown-item" href="" onClick={(event) => {
                  event.preventDefault();
                  onClickToggleHistory();
                }}>
                  <i className="fas fa-clock-rotate-left dropdown-item-icon mr-1" />
                  {l("Historial%GA067")}
                </a>
                <a className="dropdown-item" href="" onClick={(event) => {
                  event.preventDefault();
                  onClickToggleRules();
                }}>
                  <i className="fas fa-sliders dropdown-item-icon mr-1" />
                  {l("Reglas%PA102")}
                </a>
              </div>
            </div>
          }

        </div>
      }

      <div ref={listRef}
        className="chat-messages"
        onScroll={onChatScroll}>

        <div className="chat-messages-wrapper">

          {messages.map((message, index) => {

            const settings = message.settings;
            const nextMessage = messages[index + 1];

            // The placeholder is only worth showing while it is still the last message.
            if ((settings?.is_loading_message ?? false) && nextMessage != null)
              return null;

            // A step left behind to say the work goes on. It has nothing to read,
            // so it borrows the animation the live stream uses.
            if (settings?.thinking_loading)
              return <ConversationStreamingMessage
                key={index}
                agentName={chatSettings?.agent_name?.trim() || DEFAULT_AGENT_NAME}
                thinkingText={message.content?.plain || ""}
                isStreaming={true}
              />;

            if (settings?.is_result && settings.result instanceof Array && settings.result.length > 1)
              return <ConversationResult
                key={index}
                message={settings.message}
                result={settings.result}
                schema={settings.schema}
              />;

            if (settings?.is_execution_result)
              return <ConversationExecution
                key={index}
                executionId={settings.execution_id}
                message={message}
                isShown={true}
              />;

            if (!message.content) return <div key={index}></div>;

            return <ConversationMessage
              key={index}
              id={message.id}
              action={message.action}
              chatSettings={chatSettings}
              content={message.content}
              lastMessage={lastMessage}
              messageSettings={settings}
              models={models}
              nextMessage={nextMessage}
              prevMessage={messages[index - 1]}
              type={message.type}
              isHighlighted={isHighlightEnabled && lastBotMessage?.id == message.id}
              onClickRegenerateMessage={onClickRegenerateMessage}
              onClickRepeatExecution={onClickRepeatExecution}
            />;
          })}

          {isLoading && !isStreaming && !isThinkingShown &&
            <div className="loading animate__animated animate__fadeIn animate__faster">
              <div className="loader"></div>
            </div>}

          {isStreaming && !isThinkingShown &&
            <ConversationStreamingMessage
              activityText={activityText}
              agentName={chatSettings?.agent_name?.trim() || DEFAULT_AGENT_NAME}
              streamingText={streamingText}
              thinkingText={thinkingText}
              isStreaming={isStreaming}
            />}

        </div>

      </div>

      {historyComponent}
      {rulesComponent}

    </div>

    {!isReadOnly &&
      <div id="chat-footer" className="w-100">
        <div className="w-100 position-relative">

          <div className="suggestion-container d-flex align-items-end justify-content-end w-100 pb-2 pb-sm-0">
            <div>
              {scrollDirection &&
                <button
                  type="button"
                  className="scroll-to-btn text-center"
                  onClick={onClickScrollBtn}>
                  <i className={"fas " + (scrollDirection == "TO_TOP" ? "fa-arrow-up" : "fa-arrow-down") + " mr-2"} />
                </button>
              }
            </div>
          </div>

          <ConversationInput
            attachmentOptions={attachmentOptions}
            lastBotMessage={lastBotMessage}
            chatSettings={chatSettings}
            flowId={flowId}
            formRef={formRef}
            isStopShown={isStopShown}
            isLoading={isLoading}
            isPreview={isPreview}
            takePhoto={takePhoto}
            takeScreenshot={takeScreenshot}
            onSendMessage={onSendMessage}
            onClickRollbackExecution={onClickRollbackExecution}
            onClickCancelChatAction={onClickCancelChatAction}
          />

        </div>
      </div>
    }

    {/* Files dragged over the chat, waiting to be dropped into the next answer */}
    {isDraggingFiles &&
      <div className="chat-drop-overlay d-flex flex-column align-items-center justify-content-center"
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={onDragLeaveFiles}
        onDrop={onDropFiles}>
        <i className="fas fa-cloud-arrow-up mb-3" />
        <span>{l("Suelta los archivos para adjuntarlos%CH047")}</span>
      </div>}

    {cameraRequest &&
      <ConversationCamera
        onCancel={() => onFinishPhoto(null)}
        onCapture={onFinishPhoto}
      />}

    {/* The screen the user is pointing at, while they pick a region to attach */}
    {screenshotRequest &&
      <ScreenshotHelperOverlay
        rootNode={screenshotRequest.rootNode}
        onCancel={() => onFinishScreenshot(null)}
        onCapture={onFinishScreenshot}
      />}

    {/* Execution lifecycle events for the whole flow */}
    <WSManager
      initialRoom={executionRoomName}
      autoSuscribe={true}
      onMessageReceived={onExecutionMessageReceived}
    />

    {/* Message and stream signals for this chat */}
    <WSManager
      initialRoom={chatRoomName}
      autoSuscribe={true}
      onMessageReceived={onChatMessageReceived}
    />

  </div>
);

export default ConversationView;
